import { format, parse, isValid } from 'date-fns';
import _ from 'lodash';
import React, { useState, useEffect, useContext, useCallback, useRef, useMemo } from 'react';
import Icon from '@mdi/react';
import {
    mdiCheck,
    mdiCircleOffOutline,
    mdiExclamationThick,
    mdiCancel,
    mdiLightningBolt,
    mdiPlus,
    mdiPriorityLow,
    mdiTrendingUp,
} from '@mdi/js';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';
import { Tooltip } from '../../base';
import { ISelectOption } from '../../base/select';
import { Popover } from '../../base/popover';
import { CoreFetch } from '../../../api';
import { UserApi, MessageApi } from '../../../api/server';
import { formatDate, MessagingContext, sortCategoryOptions } from '../../../services';
import {
    AssigneePicker,
    IAssigneeOption,
    assigneeAvatarColor,
    formatAssigneeRole,
} from '../assignee-picker';
import { CategoryPicker } from '../category-picker';
import * as S from './message-fields-redesigned.styles';

export interface IMessageFieldsValues {
    priority?: string;
    dueDate?: string; // Format: yyyy-MM-dd
    categoryIds?: string[];
    assignedUserIds?: string[];
}

const PRIORITY_OPTIONS: ISelectOption<string>[] = [
    { value: undefined, label: 'No priority' },
    { value: 'LOW', label: 'Low' },
    { value: 'NORMAL', label: 'Normal' },
    { value: 'HIGH', label: 'High' },
    { value: 'URGENT', label: 'Urgent' },
];

const PRIORITY_ICONS: Record<string, string> = {
    LOW: mdiPriorityLow,
    NORMAL: mdiTrendingUp,
    HIGH: mdiLightningBolt,
    URGENT: mdiExclamationThick,
    NONE: mdiCancel,
};
const PRIORITY_ICON_DEFAULT = mdiCircleOffOutline;

export interface IMessageFieldsRedesignedProps {
    values: IMessageFieldsValues;
    onChange: (values: IMessageFieldsValues) => void;
    isCategorySaving?: boolean;
    isAssignedUsersSaving?: boolean;
    read?: boolean;
    onReadChange?: (v: boolean) => void;
    readDisabled?: boolean;
    done?: boolean;
    onDoneChange?: (v: boolean) => void;
    doneDisabled?: boolean;
    hideDone?: boolean;
}

export const MessageFieldsRedesigned: React.FunctionComponent<IMessageFieldsRedesignedProps> = props => {
    const {
        values,
        onChange,
        isCategorySaving,
        isAssignedUsersSaving,
        read = false,
        onReadChange,
        readDisabled,
        done = false,
        onDoneChange,
        doneDisabled,
        hideDone = false,
    } = props;
    const context = useContext(MessagingContext);
    const isSupportApp = context.account?.supportGrant === true;
    const currentAccount = context.account?.accountAccess?.find(
        acc => acc.accountId === context.account?.grantedAccountId,
    );
    const isCodingAccessOnly = currentAccount?.roleId === 'CODING_ACCESS';

    const [categoryOptions, setCategoryOptions] = useState<Array<{ value: string; label: string }>>([]);
    const [assigneeOptions, setAssigneeOptions] = useState<IAssigneeOption[]>([]);
    const [dateOpen, setDateOpen] = useState(false);
    const [priorityOpen, setPriorityOpen] = useState(false);

    /** Guard: only one toggle handler runs per user gesture (avoids both READ/DONE firing on one click) */
    const toggleHandlingRef = useRef<'read' | 'done' | null>(null);
    const wrapReadChange = useCallback(
        (v: boolean) => {
            if (toggleHandlingRef.current !== null) return;
            toggleHandlingRef.current = 'read';
            onReadChange?.(v);
            setTimeout(() => {
                toggleHandlingRef.current = null;
            }, 0);
        },
        [onReadChange],
    );
    const wrapDoneChange = useCallback(
        (v: boolean) => {
            if (toggleHandlingRef.current !== null) return;
            toggleHandlingRef.current = 'done';
            onDoneChange?.(v);
            setTimeout(() => {
                toggleHandlingRef.current = null;
            }, 0);
        },
        [onDoneChange],
    );

    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const categories = isSupportApp
                    ? await CoreFetch(MessageApi).listSupportMessageCategory()
                    : await CoreFetch(MessageApi).listProviderMessageCategory();
                setCategoryOptions(
                    sortCategoryOptions(_.map(categories, cat => ({ value: cat.id || '', label: cat.name || '' }))),
                );
            } catch (e) {
                console.error('Failed to load message categories:', e);
            }
            if (!isCodingAccessOnly) {
                try {
                    const users = await CoreFetch(UserApi).listProviderUsers({});
                    const grantedAccountId = context.account?.grantedAccountId;
                    const myUserId = context.account?.personalAccountId;
                    setAssigneeOptions(
                        _.compact(
                            _.map(users, user => {
                                const id = user.personalAccountId;
                                if (!id) {
                                    return null;
                                }
                                const name =
                                    `${user.first || ''} ${user.last || ''}`.trim() ||
                                    user.email ||
                                    'Unknown';
                                const roleId = user.roles?.find(r => r.accountId === grantedAccountId)
                                    ?.roleId;
                                return {
                                    id,
                                    name,
                                    role: formatAssigneeRole(roleId) || user.email,
                                    isYou: id === myUserId,
                                    color: assigneeAvatarColor(id),
                                } as IAssigneeOption;
                            }),
                        ),
                    );
                } catch (e) {
                    console.error('Failed to load users:', e);
                }
            }
        };
        fetchOptions();
    }, [isSupportApp, isCodingAccessOnly, context.account?.grantedAccountId, context.account?.personalAccountId]);

    const handlePriorityChange = useCallback(
        (option: ISelectOption<string> | null) => {
            onChange({ ...values, priority: option?.value });
        },
        [values, onChange],
    );

    const handleDueDateChange = useCallback(
        (date: Date | undefined) => {
            onChange({ ...values, dueDate: date ? format(date, 'yyyy-MM-dd') : undefined });
            setDateOpen(false);
        },
        [values, onChange],
    );

    const handleCategoryToggle = useCallback(
        (categoryId: string) => {
            const current = values.categoryIds || [];
            const next = current.includes(categoryId)
                ? current.filter(id => id !== categoryId)
                : [...current, categoryId];
            onChange({ ...values, categoryIds: next });
        },
        [values, onChange],
    );

    const handleAssigneeToggle = useCallback(
        (userId: string) => {
            const current = values.assignedUserIds || [];
            const next = current.includes(userId)
                ? current.filter(id => id !== userId)
                : [...current, userId];
            onChange({ ...values, assignedUserIds: next });
        },
        [values, onChange],
    );

    const handleAssigneeClear = useCallback(() => {
        onChange({ ...values, assignedUserIds: [] });
    }, [values, onChange]);

    const selectedDate = values.dueDate
        ? (() => {
              const p = parse(values.dueDate, 'yyyy-MM-dd', new Date());
              return isValid(p) ? p : undefined;
          })()
        : undefined;

    const priorityOption = PRIORITY_OPTIONS.find(o => o.value === values.priority) || PRIORITY_OPTIONS[0];
    const selectedAssigneeIds = values.assignedUserIds || [];
    const composeAssigneeOptions = useMemo(() => {
        const knownIds = new Set(assigneeOptions.map(option => option.id));
        const extras = selectedAssigneeIds
            .filter(id => !knownIds.has(id))
            .map(id => ({
                id,
                name: id,
                color: assigneeAvatarColor(id),
            }));
        return [...extras, ...assigneeOptions];
    }, [assigneeOptions, selectedAssigneeIds]);

    return (
        <S.FieldsGrid>
            {/* Row 1: READ | Due date | Assign to */}
            <S.FieldRow key="message-fields-row-1">
                {onReadChange !== undefined && (
                    <S.LabeledField key="read-field">
                        <S.FieldLabelTop>Read</S.FieldLabelTop>
                        <S.ToggleWrapper>
                            <S.MessageFieldsToggle
                                    value={!!read}
                                    onChange={wrapReadChange}
                                    disabled={!!readDisabled}
                                />
                            </S.ToggleWrapper>
                    </S.LabeledField>
                )}
                <S.LabeledField gap={4}>
                    <S.FieldLabelTop>Due date</S.FieldLabelTop>
                    <Popover
                        open={dateOpen}
                        onOpenChange={setDateOpen}
                        align="left"
                        renderButton={() =>
                            values.dueDate ? (
                                <Tooltip direction="top" title={<span>Due Date</span>}>
                                    <S.DropdownTrigger>
                                        <S.DropdownTriggerValue>
                                            {formatDate(values.dueDate, 'MMM d, yyyy')}
                                        </S.DropdownTriggerValue>
                                    </S.DropdownTrigger>
                                </Tooltip>
                            ) : (
                                <S.EmptyFieldTrigger type="button">
                                    <Icon path={mdiPlus} size={0.7} />
                                    Due Date
                                </S.EmptyFieldTrigger>
                            )
                        }>
                        <S.DatePopoverContainer>
                            <DayPicker
                                mode="single"
                                selected={selectedDate}
                                defaultMonth={selectedDate}
                                onSelect={handleDueDateChange}
                                captionLayout="dropdown"
                                startMonth={new Date(1900, 0)}
                                endMonth={new Date(new Date().getFullYear() + 10, 11)}
                            />
                        </S.DatePopoverContainer>
                    </Popover>
                </S.LabeledField>
                <S.LabeledField gap={4}>
                    <S.FieldLabelTop>Assign to</S.FieldLabelTop>
                    {!isCodingAccessOnly && (
                        <AssigneePicker
                            selectedIds={selectedAssigneeIds}
                            options={composeAssigneeOptions}
                            saving={!!isAssignedUsersSaving}
                            onToggle={handleAssigneeToggle}
                            onClear={handleAssigneeClear}
                        />
                    )}
                </S.LabeledField>
            </S.FieldRow>

            {/* Row 2: DONE | Priority pill | Category tags */}
            <S.FieldRow key="message-fields-row-2">
                {!hideDone && onDoneChange !== undefined && (
                    <S.LabeledField key="done-field">
                        <S.FieldLabelTop>Done</S.FieldLabelTop>
                        <S.ToggleWrapper>
                            <S.MessageFieldsToggle
                                    value={!!done}
                                    onChange={wrapDoneChange}
                                    disabled={!!doneDisabled}
                                />
                            </S.ToggleWrapper>
                    </S.LabeledField>
                )}
                <S.LabeledField gap={4}>
                    <S.FieldLabelTop>Priority</S.FieldLabelTop>
                    <Popover
                        open={priorityOpen}
                        onOpenChange={setPriorityOpen}
                        align="left"
                        verticalAlign="top"
                        renderButton={() =>
                            values.priority ? (
                                <Tooltip direction="top" title={<span>Priority</span>}>
                                    <S.PriorityPill priority={(values.priority ?? '').toUpperCase()}>
                                        <S.PriorityPillIcon>
                                            <Icon
                                                path={
                                                    PRIORITY_ICONS[(values.priority ?? '').toUpperCase()] ??
                                                    PRIORITY_ICON_DEFAULT
                                                }
                                                size={0.65}
                                            />
                                        </S.PriorityPillIcon>
                                        <span>{priorityOption.label}</span>
                                    </S.PriorityPill>
                                </Tooltip>
                            ) : (
                                <S.EmptyFieldTrigger type="button">
                                    <Icon path={mdiPlus} size={0.7} />
                                    Priority
                                </S.EmptyFieldTrigger>
                            )
                        }>
                        <S.PopoverOptionsList>
                            {PRIORITY_OPTIONS.map(opt => {
                                const isSelected = opt.value === values.priority;
                                return (
                                    <S.PopoverOption
                                        key={opt.value ?? 'none'}
                                        selected={isSelected}
                                        onClick={() => {
                                            handlePriorityChange(opt);
                                            setPriorityOpen(false);
                                        }}>
                                        <S.PopoverOptionIcon>
                                            {isSelected && <Icon path={mdiCheck} size={0.6} />}
                                        </S.PopoverOptionIcon>
                                        {opt.label}
                                    </S.PopoverOption>
                                );
                            })}
                        </S.PopoverOptionsList>
                    </Popover>
                </S.LabeledField>
                <S.LabeledField gap={4}>
                    <S.FieldLabelTop>Category</S.FieldLabelTop>
                    <CategoryPicker
                        selectedIds={values.categoryIds || []}
                        options={categoryOptions}
                        saving={!!isCategorySaving}
                        onToggle={handleCategoryToggle}
                    />
                </S.LabeledField>
            </S.FieldRow>
        </S.FieldsGrid>
    );
};
